import { observer } from "mobx-react-lite";
import { useParams } from "react-router-dom";

import { ChatLauncherSpacer } from "@/components/layout/ChatLauncherSpacer";
import { NotionStyleLayout } from "@/components/building/NotionStyleLayout";
import {
  DocumentAnalyticsUnavailable,
  DocumentsSection,
} from "@/components/building/DocumentsSection";
import { MessagesWidget } from "@/components/messages/MessagesWidget";
import { PageTitle } from "@/components/ui/page-title";
import { UploadedTodayWidget } from "@/domains/documents/components/widgets/UploadedTodayWidget";
import { MyTasksWidget } from "@/domains/pm/components/widgets/MyTasksWidget";
import { RecentChangesWidget } from "@/domains/projects/recentChanges/RecentChangesWidget";
import { BriefDashboardCard } from "@/domains/brief/components/BriefDashboardCard";
import { useBuildingDocumentStats } from "@/hooks/buildings/useBuildingDocumentStats";
import { useBuildingScope } from "@/hooks/buildings/useBuildingScope";
import { useProjectDocumentCoverage } from "@/hooks/useProjectDocumentCoverage";
import { useScrollReset } from "@/hooks/useScrollReset";
import { useLanguage } from "@/lib/i18n/LanguageContext";

/**
 * The Project Dashboard (`/buildings/:id/project-dashboard`) — always scoped
 * to the project selected from the sidebar. Composes the project's message
 * board beside what is new over a chosen timeframe, then the current user's
 * tasks beside the project brief, then the document analytics charts (whose
 * second row carries today's uploads beside the upload-activity chart).
 *
 * BuildingLayout's Projects-view gate guarantees a concrete project before
 * this page renders (loading / load-failure / no-projects / chooser states
 * are all handled there), so the null guard here is just a transient frame.
 */
const ProjectsDashboard = observer(function ProjectsDashboard() {
  const { id } = useParams<{ id: string }>();
  const { t } = useLanguage();
  useScrollReset();

  const { selectedProjectId } = useBuildingScope(id ?? "");

  const buildingIdNum = Number(id);
  const projectIdStr = selectedProjectId != null ? String(selectedProjectId) : undefined;

  const docStats = useBuildingDocumentStats(id, {
    timeRange: "30d",
    projectId: projectIdStr,
  });
  const coverage = useProjectDocumentCoverage(selectedProjectId);

  if (!id || selectedProjectId == null) return null;

  return (
    <NotionStyleLayout>
      <PageTitle info="projectDashboard">{t.buildings.projectsDashboard.title}</PageTitle>

      {/* Message board + What's new — two-column row. The grid's default
          stretch keeps both cards the same height: the taller one (each capped
          at the same max-h) sets the row, and the other fills to match. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.32fr)_minmax(0,1fr)]">
        <MessagesWidget buildingId={buildingIdNum} projectId={projectIdStr} />
        <RecentChangesWidget buildingId={buildingIdNum} projectId={selectedProjectId} />
      </div>

      {/* My tasks + Brief — plain lists, not cards. My tasks sets the row's
          height and Brief fills it (its content never adds height), so neither
          leaves empty space under the other. Either renders nothing without
          access (the PM tool, briefs) and the other takes the row; Brief alone
          gets a taller floor, since nothing beside it sets one. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.32fr)_minmax(0,1fr)] lg:[&>*:only-child]:col-span-2 [&>[data-testid=brief-dashboard-card]:only-child]:min-h-[260px]">
        <MyTasksWidget buildingId={buildingIdNum} projectId={selectedProjectId} />
        <BriefDashboardCard buildingId={buildingIdNum} projectId={selectedProjectId} />
      </div>

      {/* Document analytics — only for a viewer who sees the WHOLE project's
          documentation (owners, admins, members with no hidden folders): over
          part of a project the numbers would pass for the project's. Anyone
          else gets the title and an "i" saying why, even when their count is
          0 — their 0 is not the project's. With full access the section is
          hidden while the project has no documents, matching the building
          dashboard. "Uploaded today" is the right-hand column of its second
          row, beside Upload activity. */}
      {coverage === "partial" ? (
        <DocumentAnalyticsUnavailable t={t} />
      ) : (
        docStats.total > 0 && (
          <DocumentsSection
            buildingId={id}
            projectId={projectIdStr}
            uploadedToday={<UploadedTodayWidget buildingId={id} projectId={selectedProjectId} />}
            t={t}
          />
        )
      )}
      <ChatLauncherSpacer />
    </NotionStyleLayout>
  );
});

export default ProjectsDashboard;
